import React, { useState, useEffect } from 'react';
import { X, User, Save, AlertCircle, Upload, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { clubService } from '../services/clubService';
import api from '../services/api';

// Lista de provincias españolas
const SPANISH_PROVINCES = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz', 'Barcelona',
  'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ciudad Real', 'Córdoba', 'La Coruña',
  'Cuenca', 'Gerona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca', 'Jaén',
  'León', 'Lérida', 'Lugo', 'Madrid', 'Málaga', 'Murcia', 'Navarra', 'Orense',
  'Palencia', 'Las Palmas', 'Pontevedra', 'La Rioja', 'Salamanca', 'Santa Cruz de Tenerife',
  'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid',
  'Vizcaya', 'Zamora', 'Zaragoza'
];

// Disciplinas disponibles
const DISCIPLINES = [
  'Senderismo',
  'Escalada',
  'Alpinismo',
  'Vías ferratas',
  'Descenso de barrancos',
  'Carreras por montaña',
  'Marcha nórdica',
  'Esquí de travesía',
  'BTT',
  'Otro'
];

const MemberForm = ({ isOpen, onClose, onSuccess, memberData = null, isEditing = false }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: memberData?.name || '',
    surname: memberData?.surname || '',
    document_type: memberData?.document_type || 'dni',
    document_number: memberData?.document_number || '',
    gender: memberData?.gender || '',
    gender_custom: memberData?.gender_custom || '',
    shirt_size: memberData?.shirt_size || '',
    birth_date: memberData?.birth_date || '',
    email: memberData?.email || '',
    phone: memberData?.phone || '',
    address: memberData?.address || '',
    postal_code: memberData?.postal_code || '',
    city: memberData?.city || '',
    province: memberData?.province || '',
    country: memberData?.country || 'España',
    disciplines: memberData?.disciplines || [],
    custom_discipline: '',
    comments: memberData?.comments || '',
    password: '',
    password_confirmation: '',
    role_id: '',
    club_id: memberData?.club_id || (user?.club_id || '')
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(memberData?.photo || null);
  const [clubs, setClubs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const isSuperAdmin = user?.role?.name === 'super_admin';
  const isClubUser = user?.role?.name === 'club';

  // Cargar clubs y roles al abrir el formulario
  useEffect(() => {
    if (isOpen) {
      loadClubs();
      loadRoles();
    }
  }, [isOpen]);

  // Cargar clubs
  const loadClubs = async () => {
    if (isSuperAdmin) {
      setLoadingClubs(true);
      try {
        const response = await clubService.getClubs();
        const clubsList = response.data || response;
        setClubs(Array.isArray(clubsList) ? clubsList : []);
      } catch (error) {
        console.error('Error al cargar clubs:', error);
      } finally {
        setLoadingClubs(false);
      }
    }
  };

  // Cargar roles
  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await api.get('/users/available-roles');
      // Filtrar solo el rol de member
      const memberRole = response.data.find(role => role.name === 'member');
      setRoles(memberRole ? [memberRole] : []);
      if (memberRole && !formData.role_id) {
        setFormData(prev => ({ ...prev, role_id: memberRole.id }));
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  // Actualizar el formulario cuando cambien los datos del socio
  useEffect(() => {
    if (memberData) {
      setFormData({
        name: memberData.name || '',
        surname: memberData.surname || '',
        document_type: memberData.document_type || 'dni',
        document_number: memberData.document_number || '',
        gender: memberData.gender || '',
        gender_custom: memberData.gender_custom || '',
        shirt_size: memberData.shirt_size || '',
        birth_date: memberData.birth_date ? memberData.birth_date.split('T')[0] : '',
        email: memberData.email || '',
        phone: memberData.phone || '',
        address: memberData.address || '',
        postal_code: memberData.postal_code || '',
        city: memberData.city || '',
        province: memberData.province || '',
        country: memberData.country || 'España',
        disciplines: memberData.disciplines || [],
        custom_discipline: '',
        comments: memberData.comments || '',
        password: '',
        password_confirmation: '',
        role_id: memberData.role_id || '',
        club_id: memberData.club_id || (user?.club_id || '')
      });
      
      // Construir la URL completa de la foto si existe
      if (memberData.photo) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const photoUrl = memberData.photo.startsWith('http') 
          ? memberData.photo 
          : `${apiUrl}${memberData.photo}`;
        setPhotoPreview(photoUrl);
      } else {
        setPhotoPreview(null);
      }
    } else {
      // Si no hay datos, inicializar con valores por defecto
      setFormData(prev => ({
        ...prev,
        country: 'España',
        document_type: 'dni'
      }));
      setPhotoPreview(null);
    }
    setPhotoFile(null);
    setErrors({});
  }, [memberData, isOpen, user?.club_id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file' && name === 'photo') {
      const file = files[0];
      if (file) {
        setPhotoFile(file);
        // Crear preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDisciplineToggle = (discipline) => {
    setFormData(prev => {
      const currentDisciplines = prev.disciplines || [];
      if (discipline === 'Otro') {
        // Si se selecciona "Otro", no hacer nada especial aquí
        if (currentDisciplines.includes('Otro')) {
          return { ...prev, disciplines: currentDisciplines.filter(d => d !== 'Otro'), custom_discipline: '' };
        } else {
          return { ...prev, disciplines: [...currentDisciplines, 'Otro'] };
        }
      } else {
        if (currentDisciplines.includes(discipline)) {
          return { ...prev, disciplines: currentDisciplines.filter(d => d !== discipline) };
        } else {
          return { ...prev, disciplines: [...currentDisciplines, discipline] };
        }
      }
    });
  };

  const handleAddCustomDiscipline = () => {
    if (formData.custom_discipline.trim()) {
      setFormData(prev => ({
        ...prev,
        disciplines: [...(prev.disciplines || []), `Otro: ${prev.custom_discipline.trim()}`],
        custom_discipline: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (formData.password && formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Las contraseñas no coinciden';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'El rol es obligatorio';
    }

    if (!formData.club_id) {
      newErrors.club_id = 'El club es obligatorio';
    }

    if (formData.gender === 'otro' && !formData.gender_custom.trim()) {
      newErrors.gender_custom = 'Debes especificar el género personalizado';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Preparar datos para enviar
      const { photo, password_confirmation, custom_discipline, ...dataToSend } = formData;
      
      // Si hay un archivo de foto, agregarlo
      if (photoFile) {
        dataToSend.photo = photoFile;
      }

      // Si no se está editando, incluir la contraseña
      if (!isEditing && dataToSend.password) {
        // La contraseña se enviará
      } else {
        delete dataToSend.password;
      }

      // Eliminar password_confirmation del objeto
      delete dataToSend.password_confirmation;

      // Si no hay género personalizado, eliminar el campo
      if (dataToSend.gender !== 'otro') {
        delete dataToSend.gender_custom;
      }

      // Asegurar que disciplines sea un array válido
      if (!dataToSend.disciplines || !Array.isArray(dataToSend.disciplines) || dataToSend.disciplines.length === 0) {
        dataToSend.disciplines = null;
      }

      const result = isEditing
        ? await userService.updateUser(memberData.id, dataToSend)
        : await userService.createUser(dataToSend);
      
      onSuccess?.(result);
      onClose();
    } catch (error) {
      console.error('Error al guardar el socio:', error);
      const errorMessage = error.response?.data?.message || 'Error al guardar el socio. Inténtalo de nuevo.';
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      surname: '',
      document_type: 'dni',
      document_number: '',
      gender: '',
      gender_custom: '',
      shirt_size: '',
      birth_date: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      province: '',
      country: 'España',
      disciplines: [],
      custom_discipline: '',
      comments: '',
      password: '',
      password_confirmation: '',
      role_id: '',
      club_id: user?.club_id || ''
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <User size={24} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Socio' : 'Crear Nuevo Socio'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          )}

          {/* Información Personal */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información Personal</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Nombre"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Apellidos */}
              <div>
                <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                  Apellidos
                </label>
                <input
                  type="text"
                  id="surname"
                  name="surname"
                  value={formData.surname}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Apellidos"
                />
              </div>
            </div>

            {/* Documento de Identidad */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Documento
                </label>
                <select
                  id="document_type"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="dni">DNI</option>
                  <option value="pasaporte">Pasaporte</option>
                  <option value="nie">NIE</option>
                </select>
              </div>

              <div>
                <label htmlFor="document_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Documento
                </label>
                <input
                  type="text"
                  id="document_number"
                  name="document_number"
                  value={formData.document_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Número de documento"
                />
              </div>
            </div>

            {/* Género */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Género
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="mujer"
                    checked={formData.gender === 'mujer'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Mujer
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="hombre"
                    checked={formData.gender === 'hombre'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Hombre
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="otro"
                    checked={formData.gender === 'otro'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Otro
                </label>
              </div>
              {formData.gender === 'otro' && (
                <div className="mt-2">
                  <input
                    type="text"
                    name="gender_custom"
                    value={formData.gender_custom}
                    onChange={handleInputChange}
                    placeholder="Especificar género personalizado"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.gender_custom ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.gender_custom && (
                    <p className="mt-1 text-sm text-red-600">{errors.gender_custom}</p>
                  )}
                </div>
              )}
            </div>

            {/* Talla de camiseta y Fecha de nacimiento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="shirt_size" className="block text-sm font-medium text-gray-700 mb-2">
                  Talla de Camiseta
                </label>
                <select
                  id="shirt_size"
                  name="shirt_size"
                  value={formData.shirt_size}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona una talla</option>
                  <option value="xs">XS</option>
                  <option value="s">S</option>
                  <option value="m">M</option>
                  <option value="l">L</option>
                  <option value="xl">XL</option>
                  <option value="xxl">XXL</option>
                </select>
              </div>

              <div>
                <label htmlFor="birth_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Nacimiento
                </label>
                <input
                  type="date"
                  id="birth_date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Información de Contacto */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información de Contacto</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Correo Electrónico *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="email@ejemplo.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+34 123 456 789"
                />
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Dirección</h3>
            
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Dirección
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Calle y número"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Código Postal */}
              <div>
                <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-2">
                  Código Postal
                </label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="28001"
                  maxLength="10"
                />
              </div>

              {/* Población */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  Población
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Madrid"
                />
              </div>

              {/* Provincia */}
              <div>
                <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-2">
                  Provincia
                </label>
                <select
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona una provincia</option>
                  {SPANISH_PROVINCES.map(province => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* País */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                País
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="España"
              />
            </div>
          </div>

          {/* Foto */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Foto</h3>
            <div>
              <label htmlFor="photo" className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-1" />
                Subir Foto
              </label>
              
              {/* Preview de la foto */}
              {photoPreview && (
                <div className="mb-4">
                  <img 
                    src={photoPreview} 
                    alt="Preview de la foto" 
                    className="h-32 w-32 object-cover border border-gray-300 rounded-lg"
                  />
                </div>
              )}
              
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/jpeg,image/png,image/jpg,image/gif"
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Sube una foto del socio (JPG, PNG o GIF, máximo 2MB)
              </p>
            </div>
          </div>

          {/* Disciplinas */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Disciplinas que Practica</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {DISCIPLINES.map(discipline => (
                <label key={discipline} className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.disciplines?.includes(discipline)}
                    onChange={() => handleDisciplineToggle(discipline)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{discipline}</span>
                </label>
              ))}
            </div>
            
            {/* Campo para disciplina personalizada si "Otro" está seleccionado */}
            {formData.disciplines?.includes('Otro') && (
              <div className="mt-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="custom_discipline"
                    value={formData.custom_discipline}
                    onChange={handleInputChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomDiscipline();
                      }
                    }}
                    placeholder="Especificar disciplina personalizada"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomDiscipline}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus size={16} className="mr-1" />
                    Agregar
                  </button>
                </div>
              </div>
            )}

            {/* Lista de disciplinas seleccionadas */}
            {formData.disciplines && formData.disciplines.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Disciplinas seleccionadas:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.disciplines.map((discipline, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {discipline}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            disciplines: prev.disciplines.filter((_, i) => i !== index)
                          }));
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Comentarios */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Comentarios</h3>
            <div>
              <textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Comentarios adicionales sobre el socio..."
              />
            </div>
          </div>

          {/* Información del Sistema */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información del Sistema</h3>
            
            {/* Club */}
            <div>
              <label htmlFor="club_id" className="block text-sm font-medium text-gray-700 mb-2">
                Club *
              </label>
              {loadingClubs ? (
                <div className="text-gray-500">Cargando clubs...</div>
              ) : (
                <select
                  id="club_id"
                  name="club_id"
                  value={formData.club_id}
                  onChange={handleInputChange}
                  disabled={!isSuperAdmin}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.club_id ? 'border-red-300' : 'border-gray-300'
                  } ${!isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Selecciona un club</option>
                  {clubs.map(club => (
                    <option key={club.id} value={club.id}>
                      {club.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.club_id && (
                <p className="mt-1 text-sm text-red-600">{errors.club_id}</p>
              )}
            </div>

            {/* Contraseña (solo al crear) */}
            {!isEditing && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña *
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Mínimo 8 caracteres"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña *
                  </label>
                  <input
                    type="password"
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.password_confirmation ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Repite la contraseña"
                  />
                  {errors.password_confirmation && (
                    <p className="mt-1 text-sm text-red-600">{errors.password_confirmation}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEditing ? 'Actualizar Socio' : 'Crear Socio'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberForm;
